// Ecranul «Așezarea pe module»: ce a făcut pasul de așezare al planului de
// situație (14.10.2026) imediat după alegerea corpurilor — a mutat corpurile ca
// golurile dintre ele și fâșiile până la peretele excavației să fie lățimi de
// benzi de parcare, și a hotărât conturul excavației din zona săpabilă și din
// bugetul de teren nesăpat.
//
// Tot ce e aici vine din `diagnostics.asezare` al documentului PLANULUI, scris
// la finalizare (`positioning_plan/backend/engine/site_plan.py`). Trei vederi:
// «înainte» (corpurile cum le-a așezat căutarea și excavația cum ar fi
// dimensionat-o planul), «după» (așezarea nouă, cu benzile estimate) și «diff»
// (amândouă suprapuse: ce s-a săpat în plus verde, la ce s-a renunțat roșu,
// săgeți pe mutări).
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import * as api from "../api";
import { northUp } from "../viewer/canonical";
import "../solutions/board.css";
import { listSolutions, type SolutieRand } from "../nav/api";
import { caleRand, numeRand } from "../nav/selectie";
import SectiuneFasie from "./SectiuneFasie";
import RiglaLayer, { type Masura } from "../viewer/plan/RiglaLayer";
import { scrollIntent } from "../viewer/view";
import "./excavatie.css";

type Punct = [number, number];
type Banda = { tip: string; poly: Punct[]; locuri: number; orientare?: string };
type Gol = { nume: string; d: number; secventa: string; rest: number };
type Treapta = { latura: string; de_la: number; pana_la: number; d: number; d_inainte: number;
                 secventa: string; locuri: number; arie: number };
type CorpAsezare = { cod: string; dx: number; dy: number; ala: number; linie?: number | null;
                     coloana?: number | null };
type Ala = { persoane: number; adaposturi: number; sursa?: string;
             alese: { corp: string; adaposturi: number }[]; capacitate: Record<string, number>;
             neacoperite: number; arie_adapost?: number; umplere?: number };
type Groapa = {
  cod: string; corpuri: string[]; corpuri_idx?: number[];
  linii: number; coloane: number; u_rigid?: boolean;
  schimbat?: boolean; motiv?: string; fara_parcare?: boolean;
  scor?: number; locuri?: number; rest?: number; arie?: number; scor_estimat?: number;
  goluri?: Gol[]; trepte?: Treapta[]; corpuri_asezare?: CorpAsezare[];
  benzi?: Banda[]; alei?: Punct[][]; resturi?: Punct[][]; racorduri?: Punct[][];
  alei_izolate?: Punct[][]; retea_bucati?: number; contur?: Punct[];
  ala?: Ala; enumerare?: Record<string, unknown>; respinse?: Record<string, number>;
  A_max?: number; dmax?: Record<string, number>; cerut?: number;
};
type Raport = {
  aplicata: boolean; motiv?: string;
  corpuri?: { index: number; poly_inainte: Punct[]; poly_dupa?: Punct[]; dx?: number; dy?: number }[];
  gropi?: Groapa[]; note_rosii?: string[];
  contururi_inainte?: Punct[][]; arie_inainte?: number | null; arie_dupa?: number;
  incercari?: { gropi: number; motiv: string }[]; secunde?: number; partitie?: number[][];
  mutare_maxima?: number; corpuri_nelegate?: number[]; volume_fara_rampa?: number[];
  verde_pe_sol_sub_prag?: { pe_sol: number; prag: number };
  teren_nesapat?: { inainte: number | null; dupa: number; prag: number; rezerva: number };
  /** fallback-ul: reașezarea săpăturii (doar pereții), când cea a clădirilor nu s-a aplicat */
  sapatura?: { aplicata: boolean; motiv?: string | null; arie?: { inainte: number; dupa: number };
               subsoluri?: { code: string; schimbat: boolean; motiv?: string; laturi: Record<string, number>;
                             rotita?: Rotita; relaxata?: Relaxata }[] };
};
/** fallback-urile reașezării săpăturii: rotită față de clădiri, sau cu aleea cerută doar pe laturile lungi */
type Rotita = { rotire: number; motiv_aliniat: string };
type Relaxata = { pereche: string[]; corpuri: number; motiv_strict: string };
/** raportul reașezării săpăturii, în forma lui (`diagnostics.excavatie`) */
type LaturaSapatura = { d_inainte: number; d: number; secventa: string; rest?: number; d_max?: number; corp?: string };
type RaportSapatura = { schimbat: boolean; motiv?: string;
                        arie?: { inainte: number; dupa: number };
                        teren_nesapat?: { inainte: number; dupa: number; prag: number; rezerva?: number };
                        subsoluri?: { code: string; schimbat: boolean; motiv?: string; laturi?: Record<string, LaturaSapatura>;
                                      contur_inainte?: Punct[]; contur_dupa?: Punct[];
                                      rotita?: Rotita; relaxata?: Relaxata }[] };
type Obiect = { id?: string; type?: string; properties?: Record<string, unknown> | null;
                geometry?: { poly?: Punct[] } | null };
type Doc = { objects?: Obiect[]; diagnostics?: Record<string, unknown> | null };

/** sufixul din rândul de fallback: cum s-a ajuns la conturul ăsta, când nu e alegerea obișnuită */
const notaFallback = (s: { rotita?: Rotita; relaxata?: Relaxata }) =>
  s.rotita ? ` (rotită cu ${s.rotita.rotire > 0 ? "+" : ""}${s.rotita.rotire}°)`
  : s.relaxata ? ` (alee doar pe laturile lungi ${s.relaxata.pereche.join("/")}, ${s.relaxata.corpuri} corpuri)` : "";

const nr = (v: number | null | undefined, z = 0) =>
  v === undefined || v === null ? "—"
    : v.toLocaleString("ro-RO", { minimumFractionDigits: z, maximumFractionDigits: z });

/** Secvența în cuvinte, pentru tooltip-uri. */
function descrie(sec: string): string {
  const l = sec.split(/\s+/).filter((c) => "AaRP".includes(c) && c.length === 1);
  if (!l.length) return "peretele stă pe fațadă: fâșia nu ține nicio bandă";
  const n = (c: string) => l.filter((x) => x === c).length;
  const buc: string[] = [];
  if (n("R")) buc.push(`${n("R")} rânduri la 90°`);
  if (n("P")) buc.push(`${n("P")} rânduri paralele`);
  const alei = n("A") + n("a");
  if (alei) buc.push(`${alei} ${alei === 1 ? "alee" : "alei"}`);
  return buc.join(", ");
}

const cale = (p: Punct[]) =>
  p.length ? p.map(([x, y], i) => `${i ? "L" : "M"}${x.toFixed(2)},${y.toFixed(2)}`).join(" ") + " Z" : "";
const centru = (p: Punct[]): Punct =>
  [p.reduce((a, q) => a + q[0], 0) / Math.max(1, p.length), p.reduce((a, q) => a + q[1], 0) / Math.max(1, p.length)];

type Forma = { d: string; c: Punct };
type DesenGroapa = {
  cod: string; contur: string | null;
  randuri: (Forma & { tip: string; locuri: number })[];
  alei: string[]; resturi: string[]; racorduri: string[]; izolate: string[];
  etichete: { cod: string; ala: number; c: Punct }[];
};

export default function Excavatie() {
  const { projectId = "", solutionId = "" } = useParams();
  const [doc, setDoc] = useState<Doc | null>(null);
  const [sol, setSol] = useState<SolutieRand | null>(null);
  const [eroare, setEroare] = useState("");
  const [arata, setArata] = useState<"diff" | "inainte" | "dupa">("diff");
  // ZOOM ȘI PLIMBARE (Oana, 14.10.2026): viewBox-ul e stare; rotița mărește în
  // jurul cursorului, tragerea cu mouse-ul plimbă, «⟲» readuce tot planul
  const [vb, setVb] = useState<[number, number, number, number] | null>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const tragere = useRef<{ x: number; y: number; vb: [number, number, number, number] } | null>(null);
  // RIGLA (ca la viewer-ul de parcare): cu ea aprinsă, un clic pune primul
  // capăt, al doilea clic încheie măsura; Escape o lasă din mână
  const [rigla, setRigla] = useState(false);
  const [masuri, setMasuri] = useState<Masura[]>([]);
  const [inCurs, setInCurs] = useState<Masura | null>(null);
  const stingeRigla = () => { setRigla(false); setMasuri([]); setInCurs(null); };
  useEffect(() => {
    const laTasta = (e: KeyboardEvent) => { if (e.key === "Escape") stingeRigla(); };
    window.addEventListener("keydown", laTasta);
    return () => window.removeEventListener("keydown", laTasta);
  }, []);

  useEffect(() => {
    api.solutionDocument(projectId, solutionId).then((d) => setDoc(d as Doc))
      .catch(() => setEroare("Nu am putut încărca documentul soluției."));
    listSolutions(projectId)
      .then((r) => setSol(r.solutii.find((s) => s.id === solutionId) ?? null))
      .catch(() => undefined);
  }, [projectId, solutionId]);

  const rap = (doc?.diagnostics?.asezare ?? null) as Raport | null;
  const gropi = useMemo(() => rap?.gropi ?? [], [rap]);
  const sap = (doc?.diagnostics?.excavatie ?? null) as RaportSapatura | null;
  const cuSapatura = !!(rap && !rap.aplicata && rap.sapatura?.aplicata && sap?.schimbat);

  // ACELAȘI CADRU CA VIEWER-UL: `northUp` întoarce documentul o dată (est la
  // dreapta, nord în sus). Tot ce se desenează intră în același document fals,
  // ca să treacă prin aceeași transformare.
  const desen = useMemo(() => {
    if (!doc || !rap) return null;
    const objs = doc.objects ?? [];
    const inel = (o: Obiect) => (o.geometry?.poly ?? []) as Punct[];
    if (!objs.find((o) => o.type === "Site")) return null;

    const false_: Obiect[] = [];
    const pune = (id: string, poly: Punct[]) => {
      if (poly && poly.length > 2) false_.push({ id, type: "Area", geometry: { poly } });
    };
    (rap.corpuri ?? []).forEach((c) => {
      pune(`__ci_${c.index}`, c.poly_inainte);
      if (c.poly_dupa) pune(`__cd_${c.index}`, c.poly_dupa);
    });
    (rap.contururi_inainte ?? []).forEach((p, k) => pune(`__gi_${k}`, p));
    // fallback-ul: contururile reașezării săpăturii, înainte și după
    (sap?.subsoluri ?? []).forEach((sb, k) => {
      if (sb.contur_inainte) pune(`__si_${k}`, sb.contur_inainte);
      if (sb.schimbat && sb.contur_dupa) pune(`__sd_${k}`, sb.contur_dupa);
    });
    for (const g of gropi) {
      if (g.contur) pune(`__gd_${g.cod}`, g.contur);
      (g.benzi ?? []).forEach((b, i) => pune(`__b_${g.cod}_${i}`, b.poly));
      (g.alei ?? []).forEach((p, i) => pune(`__a_${g.cod}_${i}`, p));
      (g.resturi ?? []).forEach((p, i) => pune(`__r_${g.cod}_${i}`, p));
      (g.racorduri ?? []).forEach((p, i) => pune(`__k_${g.cod}_${i}`, p));
      (g.alei_izolate ?? []).forEach((p, i) => pune(`__z_${g.cod}_${i}`, p));
    }
    const fals = northUp({ crs: (doc as { crs?: unknown }).crs, objects: [...objs, ...false_] }) as { objects: Obiect[] };
    const luat = (id: string) => (fals.objects.find((o) => o.id === id)?.geometry?.poly ?? []) as Punct[];
    const dupaTip = (tip: string, fel?: string) => fals.objects
      .filter((o) => o.type === tip
        && (fel === undefined || (o.properties as { kind?: string } | null)?.kind === fel)
        && !String(o.id ?? "").startsWith("__"))
      .map(inel).filter((p) => p.length > 2);

    const teren = dupaTip("Site")[0] ?? [];
    const sapabil = dupaTip("Area", "diggable");
    const edificabil = dupaTip("Area", "buildable");
    const amprenteDoc = dupaTip("Building");
    const corpuri = (rap.corpuri ?? []).map((c) => {
      const vechi = luat(`__ci_${c.index}`), nou = c.poly_dupa ? luat(`__cd_${c.index}`) : [];
      return { index: c.index, vechi, nou, dx: c.dx ?? 0, dy: c.dy ?? 0,
               cVechi: centru(vechi), cNou: nou.length ? centru(nou) : null };
    });
    const vechiToate = cuSapatura
      ? (sap?.subsoluri ?? []).map((_, k) => luat(`__si_${k}`)).filter((p) => p.length > 2)
      : (rap.contururi_inainte ?? []).map((_, k) => luat(`__gi_${k}`));
    const sapaturaNoua = cuSapatura
      ? (sap?.subsoluri ?? []).map((sb, k) => (sb.schimbat ? luat(`__sd_${k}`) : [])).filter((p) => p.length > 2).map(cale)
      : [];
    const perGroapa: DesenGroapa[] = gropi.map((g) => {
      const corpAsz = Object.fromEntries((g.corpuri_asezare ?? []).map((c) => [c.cod, c]));
      const etichete = (g.corpuri_idx ?? []).map((idx, j) => {
        const cod = g.corpuri[j];
        const corp = corpuri.find((c) => c.index === idx);
        const p = corp?.nou?.length ? corp.nou : corp?.vechi ?? [];
        return { cod, ala: corpAsz[cod]?.ala ?? 0, c: centru(p) };
      });
      return {
        cod: g.cod, contur: g.contur ? cale(luat(`__gd_${g.cod}`)) : null,
        randuri: (g.benzi ?? []).map((b, i) => {
          const p = luat(`__b_${g.cod}_${i}`);
          return { d: cale(p), c: centru(p), tip: b.tip, locuri: b.locuri };
        }),
        alei: (g.alei ?? []).map((_, i) => cale(luat(`__a_${g.cod}_${i}`))),
        resturi: (g.resturi ?? []).map((_, i) => cale(luat(`__r_${g.cod}_${i}`))),
        racorduri: (g.racorduri ?? []).map((_, i) => cale(luat(`__k_${g.cod}_${i}`))),
        izolate: (g.alei_izolate ?? []).map((_, i) => cale(luat(`__z_${g.cod}_${i}`))),
        etichete,
      };
    });

    const toate = [teren, ...sapabil, ...corpuri.map((c) => c.vechi), ...corpuri.map((c) => c.nou),
                   ...vechiToate].flat();
    if (!toate.length) return null;
    const xs = toate.map((p) => p[0]), ys = toate.map((p) => p[1]);
    const x0 = Math.min(...xs), x1 = Math.max(...xs), y0 = Math.min(...ys), y1 = Math.max(...ys);
    const marja = Math.max(x1 - x0, y1 - y0) * 0.06 + 4;
    return {
      viewBox: `${x0 - marja} ${y0 - marja} ${x1 - x0 + 2 * marja} ${y1 - y0 + 2 * marja}`,
      teren: cale(teren), sapabil: sapabil.map(cale), edificabil: edificabil.map(cale),
      amprente: amprenteDoc.map(cale),
      corpuri: corpuri.map((c) => ({ ...c, dVechi: cale(c.vechi), dNou: c.nou.length ? cale(c.nou) : null })),
      vechi: vechiToate.map(cale), gropi: perGroapa, sapaturaNoua,
      scara: Math.max(x1 - x0, y1 - y0) / 100,
    };
  }, [doc, rap, gropi, sap, cuSapatura]);

  const vbInitial = useMemo(() => {
    if (!desen) return null;
    const [x, y, w, h] = desen.viewBox.split(" ").map(Number);
    return [x, y, w, h] as [number, number, number, number];
  }, [desen]);
  useEffect(() => { setVb(vbInitial); }, [vbInitial]);

  /** punctul din SVG de sub cursor */
  const laPunct = (e: { clientX: number; clientY: number }) => {
    const svg = svgRef.current;
    if (!svg) return null;
    const m = svg.getScreenCTM();
    if (!m) return null;
    const p = new DOMPoint(e.clientX, e.clientY).matrixTransform(m.inverse());
    return [p.x, p.y] as Punct;
  };
  /** metri de plan pe un pixel de ecran, la vederea curentă */
  const scaraPx = (v: [number, number, number, number]) => {
    const r = svgRef.current?.getBoundingClientRect();
    return Math.max(v[2] / Math.max(1, r?.width ?? 1), v[3] / Math.max(1, r?.height ?? 1));
  };
  // ROTIȚA PLIMBĂ, PINCH-UL (sau Ctrl + rotiță) MĂREȘTE — aceeași regulă ca la
  // viewer (`scrollIntent`), ca să nu existe două feluri de rotiță în aplicație
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    const laRotita = (e: WheelEvent) => {
      e.preventDefault();
      setVb((v) => {
        if (!v) return v;
        const [x, y, w, h] = v;
        if (scrollIntent(e) === "zoom") {
          const m = svg.getScreenCTM();
          if (!m) return v;
          const p = new DOMPoint(e.clientX, e.clientY).matrixTransform(m.inverse());
          const f = Math.exp(e.deltaY * 0.01);
          return [p.x - (p.x - x) * f, p.y - (p.y - y) * f, w * f, h * f];
        }
        const sc = scaraPx(v);
        return [x + e.deltaX * sc, y + e.deltaY * sc, w, h];
      });
    };
    svg.addEventListener("wheel", laRotita, { passive: false });
    return () => svg.removeEventListener("wheel", laRotita);
  }, [desen]);
  const laApasare = (e: React.PointerEvent<SVGSVGElement>) => {
    if (!vb || e.button !== 0) return;
    tragere.current = { x: e.clientX, y: e.clientY, vb };
    (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
  };
  const laMiscare = (e: React.PointerEvent<SVGSVGElement>) => {
    const t = tragere.current;
    if (rigla && inCurs && !t) {
      const q = laPunct(e);
      if (q) setInCurs({ a: inCurs.a, b: { x: q[0], y: q[1] } });
    }
    if (!t) return;
    const sc = scaraPx(t.vb);
    setVb([t.vb[0] - (e.clientX - t.x) * sc, t.vb[1] - (e.clientY - t.y) * sc, t.vb[2], t.vb[3]]);
  };
  const laRidicare = (e: React.PointerEvent<SVGSVGElement>) => {
    const t = tragere.current;
    tragere.current = null;
    if (!t || !rigla) return;
    if (Math.hypot(e.clientX - t.x, e.clientY - t.y) > 4) return;   // a fost tragere, nu clic
    const q = laPunct(e);
    if (!q) return;
    const pt = { x: q[0], y: q[1] };
    if (!inCurs) setInCurs({ a: pt, b: pt });
    else { setMasuri((v) => [...v, { a: inCurs.a, b: pt }]); setInCurs(null); }
  };

  /** RIGLA: o bară de lungime «rotundă» (10, 20, 50 … m) cam a cincea parte din
   *  lățimea vederii, jos-stânga, în unități de plan (metri) */
  const scara_bara = useMemo(() => {
    if (!vb) return null;
    const [x, y, w, h] = vb;
    const brut = w / 5;
    const trepte = [1, 2, 5, 10, 20, 50, 100, 200, 500];
    const L = trepte.reduce((best, t) => (Math.abs(t - brut) < Math.abs(best - brut) ? t : best), trepte[0]);
    const gros = w / 500;
    return { x0: x + w * 0.04, y0: y + h - h * 0.05, L, gros, font: w / 60 };
  }, [vb]);

  if (eroare) return <div className="pad"><p className="err">{eroare}</p></div>;
  if (!doc) return <div className="pad"><p className="sg-muted">Se încarcă…</p></div>;

  const multe = gropi.length > 1;
  const noteRosii = rap?.note_rosii ?? [];
  const sapat = (gr: DesenGroapa | null) => gr?.contur;
  return (
    <div className="ob-exc">
      <div className="ob-exc-cap">
        <Link to={sol ? caleRand(projectId, sol) : `/p/${projectId}/solutii`}>
          ← {sol ? numeRand(sol) : "Soluții"}
        </Link>
        <b>Așezarea pe module · {sol ? numeRand(sol) : "…"}</b>
        {(rap?.aplicata || cuSapatura) && (
          <span className="ob-exc-comut">
            {(["inainte", "dupa", "diff"] as const).map((v) => (
              <button key={v} className={arata === v ? "on" : ""} onClick={() => setArata(v)}>
                {v === "inainte" ? "înainte" : v === "dupa" ? "după" : "diff"}
              </button>
            ))}
            <button title="tot planul" onClick={() => setVb(vbInitial)}>⟲</button>
            <button className={rigla ? "on" : ""} aria-pressed={rigla}
                    title={rigla ? "Stinge rigla (Escape); măsurile se șterg" : "Riglă: clic pe primul capăt, clic pe al doilea; Escape o lasă din mână"}
                    onClick={() => (rigla ? stingeRigla() : setRigla(true))}>
              Riglă{masuri.length ? ` · ${masuri.length}` : ""}
            </button>
          </span>
        )}
      </div>

      {!rap && (
        <p className="sg-muted pad">
          Soluția asta n-are raport de așezare: a fost generată înainte ca pasul să existe.
        </p>
      )}

      {rap && !rap.aplicata && (
        <p className="ob-exc-motiv">
          <b>Reașezarea clădirilor și săpăturii nu s-a aplicat.</b> {rap.motiv}
          {rap.sapatura?.aplicata ? (
            <> <b>Fallback: reașezarea săpăturii a mutat pereții</b>
               {rap.sapatura.arie ? ` (excavația ${nr(rap.sapatura.arie.inainte)} → ${nr(rap.sapatura.arie.dupa)} m²)` : ""}
               {(rap.sapatura.subsoluri ?? []).filter((s) => s.schimbat).map((s) =>
                 ` · ${s.code}: ${Object.entries(s.laturi).map(([k, v]) => `${k} ${nr(v, 2)}`).join(", ")}${notaFallback(s)}`).join("")}.</>
          ) : rap.sapatura ? (
            <> Fallback-ul (reașezarea săpăturii) nu s-a aplicat nici el: {rap.sapatura.motiv}. Corpurile și excavația sunt ale planului.</>
          ) : <> Corpurile și excavația sunt ale planului.</>}
        </p>
      )}

      {noteRosii.map((n, i) => <p key={i} className="ob-exc-motiv ob-exc-rosu">⚠ {n}</p>)}

      {rap && desen && (
        <div className="ob-exc-corp">
          <svg ref={svgRef} className={`ob-exc-plan${rigla ? " masoara" : ""}`}
               viewBox={(vb ?? vbInitial ?? [0, 0, 1, 1]).join(" ")}
               preserveAspectRatio="xMidYMid meet" onPointerDown={laApasare}
               onPointerMove={laMiscare} onPointerUp={laRidicare}
               onPointerLeave={() => { tragere.current = null; }}>
            <defs>
              <pattern id="e-hasura-rest" width="2" height="2" patternUnits="userSpaceOnUse" patternTransform="rotate(-45)">
                <line x1="0" y1="0" x2="0" y2="2" className="e-hasura-rest-l" />
              </pattern>
              {/* măștile diff-ului: excavat în plus = după fără înainte; renunțat = înainte fără după */}
              <mask id="e-fara-inainte">
                <rect x="-1e6" y="-1e6" width="2e6" height="2e6" fill="white" />
                {desen.vechi.map((d, i) => <path key={i} d={d} fill="black" />)}
              </mask>
              <mask id="e-fara-dupa">
                <rect x="-1e6" y="-1e6" width="2e6" height="2e6" fill="white" />
                {desen.gropi.map((g) => g.contur && <path key={g.cod} d={g.contur} fill="black" />)}
              </mask>
            </defs>
            <path d={desen.teren} className="e-teren" />
            {desen.edificabil.map((d, i) => <path key={i} d={d} className="e-edificabil" />)}
            {desen.sapabil.map((d, i) => <path key={i} d={d} className="e-sapabil" />)}

            {arata === "diff" && rap.aplicata && (
              <g className="e-diff">
                {desen.gropi.map((g) => g.contur && (
                  <path key={g.cod} d={g.contur} className="e-diff-plus" mask="url(#e-fara-inainte)" />
                ))}
                {desen.vechi.map((d, i) => <path key={i} d={d} className="e-diff-minus" mask="url(#e-fara-dupa)" />)}
              </g>
            )}

            {arata !== "dupa" && desen.vechi.map((d, i) => <path key={i} d={d} className="e-vechi" />)}
            {arata !== "inainte" && desen.sapaturaNoua.map((d, i) => <path key={i} d={d} className="e-nou" />)}

            {arata !== "inainte" && rap.aplicata && desen.gropi.map((g) => (
              <g key={g.cod}>
                {sapat(g) && <path d={g.contur!} className="e-nou" />}
                {g.randuri.map((b, i) => <path key={i} d={b.d} className={`e-banda e-banda-${b.tip}`} />)}
                {g.alei.map((d, i) => <path key={i} d={d} className="e-alee" />)}
                {g.resturi.map((d, i) => <path key={i} d={d} className="e-rest" />)}
                {g.racorduri.map((d, i) => <path key={i} d={d} className="e-racord" />)}
                {g.izolate.map((d, i) => <path key={i} d={d} className="e-izolata" />)}
                {g.randuri.filter((b) => b.locuri > 0).map((b, i) => (
                  <text key={i} x={b.c[0]} y={b.c[1]} className="e-locuri" textAnchor="middle"
                        fontSize={1.8 * desen.scara}>{b.locuri}</text>
                ))}
              </g>
            ))}

            {arata !== "dupa" && desen.corpuri.map((c) => (
              <path key={c.index} d={c.dVechi} className={rap.aplicata && arata !== "inainte" ? "e-corp-vechi" : "e-amprenta"} />
            ))}
            {cuSapatura && !desen.corpuri.length && desen.amprente.map((d, i) => <path key={i} d={d} className="e-amprenta" />)}
            {arata !== "inainte" && rap.aplicata && desen.corpuri.map((c) => c.dNou && (
              <path key={c.index} d={c.dNou} className="e-amprenta" />
            ))}
            {arata === "diff" && rap.aplicata && desen.corpuri.filter((c) => c.cNou && Math.hypot(c.dx, c.dy) > 0.05).map((c) => (
              <g key={c.index} className="e-sageata">
                <line x1={c.cVechi[0]} y1={c.cVechi[1]} x2={c.cNou![0]} y2={c.cNou![1]} />
                <circle cx={c.cNou![0]} cy={c.cNou![1]} r={0.6 * desen.scara} />
              </g>
            ))}
            {vb && (masuri.length || inCurs) ? (
              <RiglaLayer masuri={masuri} inCurs={inCurs} pxPerM={1 / Math.max(1e-9, scaraPx(vb))} />
            ) : null}
            {scara_bara && (
              <g className="e-rigla">
                <line x1={scara_bara.x0} y1={scara_bara.y0} x2={scara_bara.x0 + scara_bara.L} y2={scara_bara.y0} strokeWidth={scara_bara.gros} />
                {[0, 0.5, 1].map((k) => (
                  <line key={k} x1={scara_bara.x0 + k * scara_bara.L} y1={scara_bara.y0 - scara_bara.gros * 3}
                        x2={scara_bara.x0 + k * scara_bara.L} y2={scara_bara.y0 + scara_bara.gros * 3} strokeWidth={scara_bara.gros} />
                ))}
                <text x={scara_bara.x0} y={scara_bara.y0 - scara_bara.gros * 5} fontSize={scara_bara.font}>0</text>
                <text x={scara_bara.x0 + scara_bara.L} y={scara_bara.y0 - scara_bara.gros * 5} fontSize={scara_bara.font} textAnchor="end">{scara_bara.L} m</text>
              </g>
            )}
            {arata !== "inainte" && rap.aplicata && desen.gropi.map((g) => g.etichete.map((e) => (
              <text key={`${g.cod}_${e.cod}`} x={e.c[0]} y={e.c[1]} className="e-cota" textAnchor="middle"
                    fontSize={2.4 * desen.scara}>
                {e.cod}{e.ala ? ` · ALA×${e.ala}` : ""}
              </text>
            )))}
          </svg>

          <div className="float card ob-incercari-panou ob-exc-panou">
            {cuSapatura && sap ? (
              <>
                <div className="lbl">Fallback · reașezarea săpăturii (doar pereții)</div>
                <table className="mono">
                  <tbody>
                    <tr><td>arie excavație</td><td>{nr(sap.arie?.inainte)} → {nr(sap.arie?.dupa)} m²</td></tr>
                    <tr><td>teren nesăpat</td>
                        <td>{nr(sap.teren_nesapat?.inainte)} → {nr(sap.teren_nesapat?.dupa)} m²</td></tr>
                    <tr><td>prag verde pe sol</td>
                        <td>{nr(sap.teren_nesapat?.prag)} m²{sap.teren_nesapat?.rezerva ? ` + rezervă ${nr(sap.teren_nesapat.rezerva)} m²` : ""}</td></tr>
                  </tbody>
                </table>
                {(sap.subsoluri ?? []).map((sb) => (
                  <div key={sb.code} className="ob-exc-groapa">
                    <div className="lbl">{sb.code} · laturile (fațadă → perete)</div>
                    {!sb.schimbat && <p className="sg-muted ob-exc-nota">a rămas a planului: {sb.motiv}</p>}
                    {sb.schimbat && (sb.rotita || sb.relaxata) && (
                      <p className="sg-muted ob-exc-nota">
                        {sb.rotita
                          ? `rotită cu ${nr(sb.rotita.rotire, 1)}° față de clădiri, fiindcă aliniată la ele nu se poate: ${sb.rotita.motiv_aliniat}`
                          : `alee cerută doar pe laturile lungi ${sb.relaxata!.pereche.join("/")} (${sb.relaxata!.corpuri} corpuri pe groapă), fiindcă pe ambele direcții nu se poate: ${sb.relaxata!.motiv_strict}`}
                      </p>
                    )}
                    {sb.schimbat && sb.laturi && (
                      <table className="mono">
                        <thead><tr><th></th><th>plan</th><th>nou</th><th>bandă</th><th>max</th></tr></thead>
                        <tbody>
                          {Object.keys(sb.laturi).sort().map((k) => {
                            const L = sb.laturi![k];
                            return (
                              <tr key={k} className={L.d !== L.d_inainte ? "castigator" : ""}>
                                <td>{k}</td>
                                <td>{nr(L.d_inainte, 2)}</td>
                                <td>{nr(L.d, 2)}</td>
                                <td title={descrie(L.secventa)}>
                                  {L.d > 0 ? <SectiuneFasie secventa={L.secventa} /> : <span>perete{L.corp ? ` · ${L.corp}` : ""}</span>}
                                </td>
                                <td className="sg-muted">{nr(L.d_max, 2)}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                  </div>
                ))}
              </>
            ) : (
              <>
                <div className="lbl">Bilanț</div>
                <table className="mono">
                  <tbody>
                    <tr><td>arie excavație</td><td>{nr(rap.arie_inainte)} → {nr(rap.arie_dupa)} m²</td></tr>
                    <tr><td>teren nesăpat</td>
                        <td>{nr(rap.teren_nesapat?.inainte)} → {nr(rap.teren_nesapat?.dupa)} m²</td></tr>
                    <tr><td>prag verde pe sol</td>
                        <td>{nr(rap.teren_nesapat?.prag)} m² + rezervă {nr(rap.teren_nesapat?.rezerva)} m²</td></tr>
                    <tr><td>mutare maximă</td><td>{nr(rap.mutare_maxima, 2)} m</td></tr>
                    <tr><td>grupări încercate</td>
                        <td>{(rap.incercari ?? []).map((i) => `${i.gropi} gropi: ${i.motiv}`).join("; ") || "—"}
                            {rap.secunde !== undefined ? ` · ${nr(rap.secunde, 1)} s` : ""}</td></tr>
                  </tbody>
                </table>
              </>
            )}

            {gropi.map((g) => (
              <div key={g.cod} className="ob-exc-groapa">
                <div className="lbl">
                  {multe ? `${g.cod} · ` : ""}{g.corpuri.join(", ")} · {g.linii} linii × {g.coloane} coloane
                  {g.schimbat && (
                    <span className="sg-muted"> · scor {nr(g.scor)} · {nr(g.locuri)} locuri/nivel · {nr(g.arie)} m²</span>
                  )}
                </div>
                {!g.schimbat && <p className="sg-muted ob-exc-nota">{g.motiv}</p>}
                {g.fara_parcare && <p className="sg-muted ob-exc-nota">doar sub corpuri, fără parcare: {g.motiv}</p>}
                {g.schimbat && !!g.goluri?.length && (
                  <table className="mono">
                    <thead><tr><th>gol / fâșie</th><th>lățime</th><th>benzi</th><th>rest</th></tr></thead>
                    <tbody>
                      {g.goluri.map((o) => (
                        <tr key={o.nume}>
                          <td>{o.nume}</td>
                          <td>{nr(o.d, 2)}</td>
                          <td title={descrie(o.secventa)}>
                            {o.d > 0 ? <SectiuneFasie secventa={o.secventa} /> : <span>perete pe fațadă</span>}
                          </td>
                          <td className={o.rest ? "ob-exc-rosu" : "sg-muted"}>{o.rest ? nr(o.rest, 2) : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                {!!g.trepte?.length && (
                  <p className="sg-muted ob-exc-nota">
                    trepte: {g.trepte.map((t) => `${t.latura} ${nr(t.d_inainte, 1)} → ${nr(t.d, 1)} m pe ${nr(t.pana_la - t.de_la, 0)} m (${t.secventa}, +${nr(t.locuri)} locuri)`).join("; ")}
                  </p>
                )}
                {g.ala && (
                  <p className="sg-muted ob-exc-nota">
                    ALA estimat: {nr(g.ala.persoane)} persoane{g.ala.sursa ? ` (${g.ala.sursa})` : ""},{" "}
                    {nr(g.ala.adaposturi)} adăposturi · alese {g.ala.alese.map((a) => `${a.corp}×${a.adaposturi}`).join(", ") || "niciunul"}
                    {g.ala.neacoperite ? ` · ${g.ala.neacoperite} fără loc` : ""}
                  </p>
                )}
                {!!g.corpuri_asezare?.length && (
                  <p className="sg-muted ob-exc-nota">
                    mutări (în reperul fațadelor): {g.corpuri_asezare.map((c) => `${c.cod} (${c.dx >= 0 ? "+" : ""}${nr(c.dx, 1)}, ${c.dy >= 0 ? "+" : ""}${nr(c.dy, 1)})`).join(", ")}
                  </p>
                )}
                {g.enumerare && (
                  <p className="sg-muted ob-exc-nota">
                    enumerare: {Object.entries(g.enumerare).filter(([k]) => ["candidati_u", "candidati_v", "perechi", "verificati", "secunde"].includes(k))
                      .map(([k, v]) => `${k} ${typeof v === "number" ? nr(v, k === "secunde" ? 2 : 0) : String(v)}`).join(", ")}
                    {g.retea_bucati !== undefined ? ` · rețea de alei: ${g.retea_bucati} ${g.retea_bucati === 1 ? "bucată" : "bucăți"}` : ""}
                  </p>
                )}
              </div>
            ))}

            <div className="ob-exc-legenda">
              <span><i className="sf-cheie sf-cheie-fatada" /> corp (punctat: înainte)</span>
              <span><SectiuneFasie secventa="A" inaltime={18} /> alee</span>
              <span><SectiuneFasie secventa="R" inaltime={18} /> rând 90°</span>
              <span><SectiuneFasie secventa="P" inaltime={18} /> paralel</span>
              <span><i className="sf-cheie sf-cheie-rest" /> rest (spațiu pierdut)</span>
              <span><i className="sf-cheie sf-cheie-racord" /> racord prin rând</span>
              <span><i className="sf-cheie sf-cheie-plus" /> săpat în plus</span>
              <span><i className="sf-cheie sf-cheie-minus" /> renunțat</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
